import React from "react";
import { connect } from "react-redux";
import { Form, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";

import ConditionalButton from "../../../layout/conditional-button";
import DeleteModal from "../../../layout/modal";
import PersonSearch from "../../../layout/select/person-search";

import { isGuid } from "../../../utils/string";
import { withNavigation } from "../../../utils/wrapper";

import { send as pageInfo, loading } from "../../../actions/page";
import {
  get as getCalendar,
  save as saveCalendar,
  destroy,
} from "../../../actions/calendar";

class Settings extends React.Component {
    constructor(props) {
        super(props);

        this.errors = {};

        this.state = {
            // data
            personUuid: null,
            style: "Individual",
            caption: "[New Calendar]",
            shared: false,
            firm: false,

            // ui
            modal: {
                delete: {
                    show: false,
                    title: "Delete Calendar",
                    body: "Are you sure you want to delete this calendar?",
                },
            },

            ...this.itemToState(props), // (mostly undefined on first render; fine)
        };

        this.props.pageInfo({ title: "Calendar", breadcrumb: "New Calendar" });
        this.props.loading(true);
    }

    itemToState = (itemOrProps) => {
        const { personUuid, style, caption, shared, person } = itemOrProps || {};
        const firm = style === "Firm";

        return {
            personUuid: personUuid ?? person ?? null,
            style: style ?? (firm ? "Firm" : "Individual"),
            caption: caption ?? "[New Calendar]",
            shared: Boolean(shared),
            firm,
        };
    };

    get uuid() {
        return this.props.params.uuid;
    }

    loadData = async () => {
        this.props.loading(true);

        if (isGuid(this.uuid)) {
            const item = await this.props.getCalendar(this.uuid);

            if (item) {
                this.props.pageInfo({ title: "Calendar", breadcrumb: item?.caption || "Calendar" });
                this.setState({
                    ...this.itemToState(item),
                });
            }
        }

        this.props.loading(false);
    };

    componentDidMount = () => {
        setTimeout(async () => {
            await this.loadData();
        }, 30);
    };

    onSaveClick = async (e) => {
        if (e?.preventDefault) e.preventDefault();

        const { personUuid, caption, shared, firm } = this.state;

        const payload = {
            uuid: isGuid(this.uuid) ? this.uuid : undefined,
            personUuid: firm ? null : personUuid,
            style: firm ? "Firm" : "Individual",
            caption,
            shared: Boolean(shared),
        };

        const result = await this.props.saveCalendar(payload);
        window.location.href = `/dashboard/settings/calendars/${result.uuid}`;
    };

    onChange = (e) => {
        const name = e?.target?.name;
        const value = e?.target?.value;

        if (!name) return;

        this.setState({
            [name]: value,
        });
    };

    onCheckChange = (e) => {
        const name = e?.target?.name;
        const checked = Boolean(e?.target?.checked);

        if (!name) return;

        this.setState((prev) => {
            // If switching to firm, clear personUuid since it doesn’t apply
            if (name === "firm" && checked) {
                return { ...prev, firm: true, personUuid: null };
            }
            return { ...prev, [name]: checked };
        });
    };

    onDeleteClick = (e) => {
        if (e?.preventDefault) e.preventDefault();

        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                delete: {
                    ...prev.modal.delete,
                    show: true,
                    title: "Delete Calendar",
                    body: "This will permanently delete the calendar. Continue?",
                },
            },
        }));
    };

    onDeleteOkModal = async (e) => {
        if (e?.preventDefault) e.preventDefault();

        // Only delete if we have a real uuid
        if (isGuid(this.uuid)) {
            await this.props.destroy(this.uuid);
        }

        this.onDeleteHideModal();
        this.props.navigate("/dashboard/settings/calendars");
    };

    onDeleteHideModal = () => {
        this.setState((prev) => ({
            modal: {
                ...prev.modal,
                delete: {
                    ...prev.modal.delete,
                    show: false,
                },
            },
        }));
    };

    get saveEnabled() {
        return !((!this.state.firm) && (!isGuid(this.state.personUuid)));
    } 

    render() {
        return (
            <>
                <Col md="8" className="float-right text-end">
                    <ConditionalButton
                        enabledVariant="outline-primary"
                        tooltip="Save Configuration"
                        display={true}
                        enabled={this.saveEnabled}
                        onEnabledClick={this.onSaveClick}
                        icon="save"
                    >
                        Save
                    </ConditionalButton>

                    <ConditionalButton
                        enabledVariant="outline-primary"
                        tooltip="Delete Configuration"
                        display={true}
                        enabled={isGuid(this.uuid)}
                        onEnabledClick={this.onDeleteClick}
                        icon="bin"
                    >
                        Delete
                    </ConditionalButton>
                </Col>

                <Col>
                    <Row>
                        <Col lg="12">
                            <Row className="g-3">
                                <Col lg="12">
                                    <h2 className="small-title">Configuration Information</h2>
                                </Col>
                            </Row>

                            <Row>
                                <Col lg="8">
                                    <div className="mb-3 top-label">
                                        <Form.Control
                                            type="text"
                                            name="caption"
                                            value={this.state.caption || ""}
                                            onChange={this.onChange}
                                        />
                                        <Form.Label>CAPTION</Form.Label>
                                    </div>
                                </Col>

                                <Col lg="2">
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={this.onCheckChange}
                                            type="switch"
                                            checked={Boolean(this.state.firm)}
                                            name="firm"
                                            label="Firm wide?"
                                        />
                                    </div>
                                </Col>

                                <Col lg="2">
                                    <div className="mb-3">
                                        <Form.Check
                                            onChange={this.onCheckChange}
                                            type="switch"
                                            checked={Boolean(this.state.shared)}
                                            name="shared"
                                            label="Public?"
                                        />
                                    </div>
                                </Col>
                            </Row>

                            {this.state.firm ? null : (
                                <Row>
                                    <Col>
                                        <div className="mb-3 top-label">
                                            <PersonSearch
                                                name="personUuid"
                                                flags={8}
                                                id="personUuid"
                                                onChange={this.onChange}
                                                value={this.state.personUuid}
                                                clearable
                                            />
                                            <Form.Label>BELONGS TO</Form.Label>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </Col>
                    </Row>
                </Col>

                <DeleteModal
                    show={this.state.modal.delete.show}
                    title={this.state.modal.delete.title}
                    body={this.state.modal.delete.body}
                    buttons={[
                        {
                            caption: "ok",
                            onEnabledClick: this.onDeleteOkModal,
                            variant: "primary",
                            display: true,
                            enabled: true,
                        },
                        {
                            default: true,
                            caption: "cancel",
                            onEnabledClick: this.onDeleteHideModal,
                            variant: "secondary",
                            display: true,
                            enabled: true,
                        },
                    ]}
                />
            </>
        );
    }
}

Settings.propTypes = {
    auth: PropTypes.object.isRequired,
}

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default withNavigation(
    connect(mapStateToProps, {
        pageInfo,
        loading,
        getCalendar,
        saveCalendar,
        destroy,
    })(Settings)
);
